import { Box, Divider, Heading, Text, Flex, useMediaQuery } from "@chakra-ui/react";
import { Fragment, useEffect } from "react";
import { useQuery } from "react-query";
import { responsiveValue } from "../../../assets/style/defaultTheme";
import { theme } from "../../../assets/style/theme";
import { getParticipation } from "../../../service";
import { lineBreakToBr } from "../../../util";
import ParticipationContentItemActivity from "./ParticipationContentItemActivity";
import ParticipationContentItemApply from "./ParticipationContentItemApply";
import ParticipationContentItemBoxed from "./ParticipationContentItemBoxed";
import ParticipationContentItemColumn from "./ParticipationContentItemColumn";
import ParticipationContentItemDefine from "./ParticipationContentItemDefine";
import ParticipationContentItemImage from "./ParticipationContentItemImage";
import ParticipationContentItemNormal from "./ParticipationContentItemNormal";
import ParticipationContentItemRecentPost from "./ParticipationContentItemRecentPost";
import ParticipationContentItemRecentPostFaq from "./ParticipationContentItemRecentPostFaq";
import ParticipationContentItemSciforum from "./ParticipationContentItemSciforum";
import ParticipationContentItemSheforum from "./ParticipationContentItemSheforum";
import ParticipationContentItemShesView from "./ParticipationContentItemShesView";
import ParticipationContentItemVideo from "./ParticipationContentItemVideo";

type Props = {
	term: string;
	slug: any;
	data: any;
};

function ParticipationContent({ term, slug, data }: Props) {
	const PTitle = (props: any) => {
		const { title } = props;
		return <Heading wordBreak="keep-all" size="4xl" color="b.bk" pb="6" lineHeight={1.4} fontWeight={title.includes("<b>") ? "normal" : "bold"} dangerouslySetInnerHTML={{ __html: title }} />;
	};

	const [isMobile] = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

	const { flexBoxDirection } = responsiveValue;

	useEffect(() => {
		document.title = isEnglish ? `${data?.title}` : `참여하기 | ${data?.title}`;
	}, [data]);

	const isEnglish = window.__LANG__ === 'en';

	return (
		<>
			{data && (
				<Box pt="30" pb="30" pl={isMobile ? "10" : "0"} pr={isMobile ? "10" : "0"} maxW="1200" m="0 auto">
					<PTitle title={data.title} />

					<Text fontSize="xl" pb="6" dangerouslySetInnerHTML={{ __html: lineBreakToBr(data.content) }}></Text>

					{data.meta.participation_대표이미지 ? <ParticipationContentItemImage data1={data.meta.participation_대표이미지} /> : <Divider color="b.lg" mt="0" mb="6" />}

					{data.meta.participation_정의컨텐츠json && <ParticipationContentItemDefine data={data.meta.participation_정의컨텐츠json} />}
					{data.meta.participation_상자컨텐츠json && <ParticipationContentItemBoxed data={data.meta.participation_상자컨텐츠json} />}

					{data.meta.participation_사업목적 && <ParticipationContentItemNormal title="사업목적" data={data.meta.participation_사업목적} />}
					{data.meta.participation_모집대상 && <ParticipationContentItemNormal title="모집대상" data={data.meta.participation_모집대상} />}
					{data.meta.participation_선발대상 && <ParticipationContentItemNormal title="선발대상" data={data.meta.participation_선발대상} />}
					{data.meta.participation_전시안내 && <ParticipationContentItemNormal title="전시안내" data={data.meta.participation_전시안내} />}
					{data.meta.participation_선발기준 && <ParticipationContentItemNormal title="선발기준" data={data.meta.participation_선발기준} />}
					{data.meta.participation_교육내용 && <ParticipationContentItemNormal title="교육내용" data={data.meta.participation_교육내용} />}
					{data.meta.participation_교육특전1 && <ParticipationContentItemColumn title="교육특전" data1={data.meta.participation_교육특전1} data2={data.meta.participation_교육특전2} />}
					{data.meta.participation_주제예시1 && <ParticipationContentItemColumn title="주제예시" data1={data.meta.participation_주제예시1} data2={data.meta.participation_주제예시2} />}
					{data.meta.participation_지원주제 && <ParticipationContentItemNormal title="지원주제" data={data.meta.participation_지원주제} />}
					{data.meta.participation_지원분야 && <ParticipationContentItemNormal title="지원분야" data={data.meta.participation_지원분야} />}
					{data.meta.participation_지원대상 && <ParticipationContentItemNormal title="지원대상" data={data.meta.participation_지원대상} />}
					{data.meta.participation_지원활동 && <ParticipationContentItemNormal title="지원활동" data={data.meta.participation_지원활동} />}
					{data.meta.participation_지원내용 && <ParticipationContentItemNormal title="지원내용" data={data.meta.participation_지원내용} />}
					{data.meta.participation_지원내용이미지 && <ParticipationContentItemImage title=" " data1={data.meta.participation_지원내용이미지} data2={data.meta.participation_지원내용이미지비고} />}
					{data.meta.participation_지원기간 && <ParticipationContentItemNormal title="지원기간" data={data.meta.participation_지원기간} />}
					{data.meta.participation_선발절차 && <ParticipationContentItemImage title="선발절차" data1={data.meta.participation_선발절차} data2={data.meta.participation_선발절차비고} />}
					{data.meta.participation_선발명단json && (
						<Flex justify="space-between" pt="10" pb="3" direction={flexBoxDirection}>
							<Heading size="2xl" color="b.bk" pb={isMobile ? "4" : "0"}>
								선발명단
							</Heading>
							<Box color="b.g" w={isMobile ? "100%" : "74%"}>
								{data.meta.participation_선발명단json.map((item: any, index: number) => (
									<Fragment key={index}>
										<Flex justify="space-between" direction={flexBoxDirection}>
											<Heading size="2xl" color="b.g" pt="1" pb={isMobile ? "2" : "0"}>
												{item.label}
											</Heading>
											<Box color="b.lg" w={isMobile ? "100%" : "74%"}>
												{item.list.map((subitem: any, index: number) => (
													<Flex key={index} pt="1" pb="1" direction={flexBoxDirection}>
														<Heading size="xl" color="b.g" pr="2" pb={isMobile ? "2" : "0"}>
															{subitem.name}
														</Heading>
														<Text size="md" color="g.lg">
															{subitem.text}
														</Text>
													</Flex>
												))}
											</Box>
										</Flex>
										<Divider color="b.lg" mt="6" mb="6" variant="dashed" />
									</Fragment>
								))}
							</Box>
						</Flex>
					)}
					{data.meta.participation_선발명단2형json && (
						<>
							<Divider color="b.lg" mt="6" mb="10" variant="dashed" />
							<Heading size="4xl" color="b.dgr" pb="8">
								선발명단
							</Heading>
							{data.meta.participation_선발명단2형json.map((item: any, index: number) => (
								<Fragment key={index}>
									<Flex justify="space-between" direction={flexBoxDirection}>
										<Heading size="4xl" color="b.gr" pt="1" pb={isMobile ? "4" : "0"}>
											{item.label}
										</Heading>
										<Box color="b.lg" w={isMobile ? "100%" : "74%"}>
											{item.list.map((subitem: any, index: number) => (
												<>
													<Flex key={index} pt="1" pb="1" direction={flexBoxDirection}>
														<Heading w="20%" size="xl" color="b.bk" pr="2" pb={isMobile ? "2" : "0"}>
															{subitem.name}
														</Heading>
														<Text size="md" color="b.g" dangerouslySetInnerHTML={{ __html: subitem.text }} />
													</Flex>
													<Divider color="b.lg" variant="dashed" mt="4" mb="4" />
												</>
											))}
										</Box>
									</Flex>
									<Divider color="b.lg" mt="6" mb="6" variant="dashed" />
								</Fragment>
							))}
						</>
					)}
					{data.meta.participation_기수별현황json && (
						<Flex justify="space-between" pt="10" pb="3">
							<Heading size="2xl" color="b.bk">
								기수별현황
							</Heading>
							<Box color="b.g" w="74%">
								{data.meta.participation_기수별현황json.map((item: any, index: number) => (
									<Fragment key={index}>
										<Flex justify="space-between">
											<Heading size="2xl" color="b.g" pt="1">
												{item.year}년 [{item.phase}기]
											</Heading>
											<Box color="b.lg">
												<Heading size="4xl" color="b.dgr" pt="1">
													{item.students}명 수강 / {item.completed}명 수료
												</Heading>
											</Box>
										</Flex>
										<Divider color="b.lg" mt="6" mb="6" variant="dashed" />
									</Fragment>
								))}
							</Box>
						</Flex>
					)}

					{data.meta.participation_선정팀현황json && (
						<Flex justify="space-between" pt="10" pb="3" direction={flexBoxDirection}>
							<Heading size="4xl" color="b.dgr" pb={isMobile ? "4" : "0"}>
								선정팀 현황
							</Heading>
							<Box color="b.g" w="74%">
								{data.meta.participation_선정팀현황json.map((item: any, index: number) => (
									<Fragment key={index}>
										<Flex justify="space-between" direction={flexBoxDirection}>
											<Heading size="2xl" color="b.g" pt="1" pb={isMobile ? "4" : "0"}>
												{item.name}
											</Heading>
											<Box color="b.lg">
												<Flex align="center">
													<Heading size={isMobile ? "4xl" : "6xl"} color="b.dgr">
														{item.value}
													</Heading>
													<Text fontSize={isMobile ? "2xl" : "4xl"} color="b.dgr" pl="2">
														{item.ea}
													</Text>
												</Flex>
											</Box>
										</Flex>
										<Divider color="b.lg" mt="6" mb="6" variant="dashed" />
									</Fragment>
								))}
							</Box>
						</Flex>
					)}

					{data.meta.participation_지원현황json && (
						<Flex justify="space-between" pt="10" pb="3" direction={flexBoxDirection}>
							<Heading size="4xl" color="b.dgr" pb={isMobile ? "8" : "0"}>
								{ isEnglish ? 'Overview' : '지원현황'}
							</Heading>
							<Box color="b.g" w={isMobile ? "100%" : "74%"}>
								{data.meta.participation_지원현황json.map((item: any, index: number) => (
									<Fragment key={index}>
										<Flex justify="space-between" direction={flexBoxDirection}>
											<Heading size="2xl" color="b.g" pt="1" pb={isMobile ? "4" : "0"}>
												{item.name}
											</Heading>
											<Box color="b.lg">
												<Flex align="center">
													<Heading size={isMobile ? "4xl" : "6xl"} color="b.dgr">
														{item.value}
													</Heading>
													<Text fontSize={isMobile ? "2xl" : "4xl"} color="b.dgr" pl="2">
														{item.ea}
													</Text>
													{item.value2 && (
														<>
															<Heading size={isMobile ? "4xl" : "6xl"} color="b.dgr" pl="6">
																{item.value2}
															</Heading>
															<Text fontSize={isMobile ? "2xl" : "4xl"} color="b.dgr" pl="2">
																{item.ea2}
															</Text>
														</>
													)}
												</Flex>
											</Box>
										</Flex>
										<Divider color="b.lg" mt="6" mb="6" variant="dashed" />
									</Fragment>
								))}
							</Box>
						</Flex>
					)}

					{data.meta.participation_전시현황json && (
						<Flex justify="space-between" pt="10" pb="3" direction={flexBoxDirection}>
							<Heading size="4xl" color="b.dgr" pb={isMobile ? "4" : "0"}>
								전시현황
							</Heading>
							<Box color="b.g" w={isMobile ? "100%" : "74%"}>
								{data.meta.participation_전시현황json.map((item: any, index: number) => (
									<Fragment key={index}>
										<Flex justify="space-between" direction={flexBoxDirection}>
											<Heading size="2xl" color="b.g" pt="1" pb={isMobile ? "4" : "0"}>
												{item.name}
											</Heading>
											<Box color="b.lg">
												<Flex align="center">
													<Heading size={isMobile ? "4xl" : "6xl"} color="b.dgr">
														{item.value}
													</Heading>
													<Text fontSize={isMobile ? "2xl" : "4xl"} color="b.dgr" pl="2">
														{item.ea}
													</Text>
												</Flex>
											</Box>
										</Flex>
										<Divider color="b.lg" mt="6" mb="6" variant="dashed" />
									</Fragment>
								))}
							</Box>
						</Flex>
					)}

					{/* 영문 */}
					{data.meta.participation_en_target && <ParticipationContentItemNormal title="target" data={data.meta.participation_en_target} />}
					{data.meta.participation_en_selection && <ParticipationContentItemNormal title="Selection Criteria" data={data.meta.participation_en_selection} />}
					{data.meta.participation_en_support && <ParticipationContentItemNormal title="support" data={data.meta.participation_en_support} />}
					{data.meta.participation_en_period && <ParticipationContentItemNormal title="Application period" data={data.meta.participation_en_period} />}
					{data.meta.participation_en_focus && <ParticipationContentItemNormal title="Focus Areas" data={data.meta.participation_en_focus} />}
					{data.meta.participation_en_apply && <ParticipationContentItemNormal title="Who Can Apply" data={data.meta.participation_en_apply} />}
					{data.meta.participation_en_funding && <ParticipationContentItemNormal title="Funding" data={data.meta.participation_en_funding} />}


					{data.meta.participation_en_process && 
					<ParticipationContentItemImage title="process" data1={data.meta.participation_en_process} data2={data.meta.participation_선발절차비고} />}

					{data.meta.participation_en_howto && <ParticipationContentItemNormal title="How To Apply" data={data.meta.participation_en_howto} />}

					{data.meta.participation_form_id && data.meta.participation_form_id !== "0" && <ParticipationContentItemApply formId={data.meta.participation_form_id} />}

					{data.meta.participation_activity_category && data.meta.participation_activity_category !== "0" && <ParticipationContentItemActivity activity_cateogry={data.meta.participation_activity_category} />}

					{data.meta.participation_recent_post && data.meta.participation_recent_post !== "0" && <ParticipationContentItemRecentPost boardId={data.meta.participation_recent_post} />}

					{data.meta.participation_recent_post_faq && data.meta.participation_recent_post_faq !== "0" && <ParticipationContentItemRecentPostFaq boardId={data.meta.participation_recent_post_faq} />}

					{data.meta.participation_youtube_playlist && data.meta.participation_youtube_playlist !== "0" && <ParticipationContentItemVideo playlistId={data.meta.participation_youtube_playlist} />}

					{term === "04" && slug === "04" && <ParticipationContentItemSciforum />}

					{term === "04" && slug === "03" && <ParticipationContentItemShesView />}

					{((term === "04" && slug === "01") || (term === "04" && slug === "02") || (term === "04" && slug === "05") || (term === "01" && slug === "04") || (term === "01" && slug === "03") || (term === "04" && slug === "07")) && (
						<ParticipationContentItemSheforum />
					)}

					
					
				</Box>
			)}
		</>
	);
}

ParticipationContent.defaultProps = {};

export default ParticipationContent;
